import React, { useEffect, useState } from 'react';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import { logger } from '@havenengineering/module-haven-logging';
import getClient from '@havenengineering/module-owners-content';
import { Button } from '@havenengineering/module-shared-owners-ui/dist/components/Button';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { DateTime } from 'luxon';

import CategoryView from '../../components/Article/CategoryView/CategoryView';
import SingleRead from '../../components/Article/SingleRead/SingleRead';
import { BackButton } from '../../components/BackButton/BackButton';
import {
  addParamsToContentfulImage,
  transformImageParams,
} from '../../helpers/contentful-image-api';
import { fetchWrapper, withApiBaseUrl } from '../../helpers/fetch';
import { newsArticleCtaClick, pageView } from '../../helpers/googleTag';
import styles from '../../styles/pages/news-and-information.module.scss';

type DetailedArticleProps = {
  article: Article;
};

const DetailedArticle = ({ article }: DetailedArticleProps) => {
  const router = useRouter();
  const { gtmPageViewAuthInfo } = useAuthContext();
  const [ctaClicked, setCtaClicked] = useState<boolean>(false);

  useEffect(() => {
    if (article) {
      const { id, title, date, importantRead, category } = article;

      const formattedTitle = title
        .trim()
        .replace(/\s+/g, '-')
        .toLocaleLowerCase();

      const articleDaysSincePublication = DateTime.now()
        .diff(DateTime.fromISO(date), 'days')
        .toObject().days;

      pageView(
        'news and info',
        `/ga-virtual/owners-account/news-and-info/article/${formattedTitle}`,
        'owners account',
        gtmPageViewAuthInfo,
        {
          authStatus: 'logged in',
          articleId: id,
          articleTitle: title.trim().toLocaleLowerCase(),
          articleTopic: category.toLocaleLowerCase(),
          articlePublicationDate: DateTime.fromISO(date).toISODate(),
          articleDaysSincePublication: Math.floor(
            articleDaysSincePublication || 0
          ),
          articleImportantFlag: importantRead
            ? 'flagged important'
            : 'not flagged important',
        }
      );

      fetchWrapper(withApiBaseUrl(`/article/${id}/read`), {
        method: 'POST',
        credentials: 'include',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article]);

  const handleCtaClick = () => {
    newsArticleCtaClick();
    setCtaClicked(true);
  };

  return (
    <>
      <BackButton handleClick={() => router.push('/news-and-information')}>
        News and information
      </BackButton>
      <SingleRead article={article} detailView={true} />

      <div className={styles.articleBody}>
        <div>
          {article.heroImage &&
            (article?.heroImage.includes('mp4') ? (
              <video width={500} controls>
                <source src={`https:${article.heroImage}`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={addParamsToContentfulImage(
                  article.heroImage,
                  transformImageParams({
                    width: 500,
                    quality: 95,
                  })
                )}
                alt={article.title}
              />
            ))}
        </div>
        <div className="owners-body">
          {article?.body ? parse(DOMPurify.sanitize(article.body)) : ''}
        </div>
        {article?.ctaText && (
          <div className={styles.ctaContainer}>
            <Button
              className={styles.helpCta}
              data-testid="help-button"
              variant="contained"
              onClick={() => handleCtaClick()}
              disabled={ctaClicked}
            >
              {ctaClicked && article.ctaCompletedText
                ? article.ctaCompletedText
                : article.ctaText}
            </Button>
          </div>
        )}
      </div>

      <CategoryView showLinkToNews />
    </>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  if (!context?.params?.id) throw new Error('No id provided');
  if (!process.env.CONTENTFUL_ACCESS_TOKEN || !process.env.CONTENTFUL_SPACE_ID)
    throw new Error('No contentful token provided');

  const client = getClient({
    accessToken: process.env.CONTENTFUL_ACCESS_TOKEN,
    space: process.env.CONTENTFUL_SPACE_ID,
  });

  try {
    const article = await client.newsAndInformations.getArticleById(
      String(context.params.id)
    );

    return {
      props: {
        article,
        pageTitle: 'Detailed article',
      },
    };
  } catch (error) {
    const message = 'Owners hub article could not be found';
    logger.error(message, {
      error,
    });

    return { notFound: true };
  }
};

export default DetailedArticle;
